import React, { useCallback, useState } from "react";

import SquareRoundedIcon from "@mui/icons-material/SquareRounded";
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  Link,
  Paper,
  SxProps,
  TextareaAutosize,
  TextField,
  TextFieldProps,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import { DateValidationError, DesktopDatePicker } from "@mui/x-date-pickers";
import classNames from "classnames";
import { DealCreate } from "components/modals";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useCanCreateDeal, usePrompt } from "hooks";
import { isEqual, startCase } from "lodash";
import {
  useClientIdSelector,
  useDateFormatSelect,
  useDealIdSelector,
  useManagersSelect,
  useTaskDealsSelect,
  useTaskManageActions
} from "store";
import { DealBase, TaskPriority, TaskStatus, UserLight } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

// import { useModals } from "hooks";
import { taskPrioritiesOptions, taskStatusOptions } from "../../../constants";

const priorityColors: Record<TaskPriority, string> = {
  medium: "rgba(38, 166, 174, 0.45)",
  high: "#fad07c",
  low: "#c5da7d"
};

const statusColors: Record<TaskStatus, string> = {
  open: "#e0e0e0",
  done: "#b2eaad",
  "in-progress": "#b2d6f8"
};

const URL_REGEX = /(https?:\/\/[^\s]+)/g;
// Deal-page links (…/main/deal/{id}) are shown as a "Go to deal" label instead of the raw URL.
const DEAL_URL_REGEX = /\/main\/deal\/\d+/;

// Render description text with detected URLs turned into links. Text segments are
// rendered as React children (auto-escaped), so this is XSS-safe — no raw HTML.
const renderDescriptionWithLinks = (text: string): React.ReactNode =>
  text.split(URL_REGEX).map((part, index) =>
    /^https?:\/\//.test(part) ? (
      <Link key={index} href={part} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
        {DEAL_URL_REGEX.test(part) ? "Go to deal" : part}
      </Link>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  );

const readDescriptionStyle: SxProps = {
  minHeight: 82,
  maxHeight: 300,
  overflowY: "auto",
  width: "100%",
  borderRadius: 8,
  border: "1px solid #5d7782",
  padding: 10,
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  boxSizing: "border-box"
};

export type FieldsState = {
  title: string;
  deal: DealBase | null;
  assignee: UserLight | null;
  status: SelectType<TaskStatus>;
  priority: SelectType<TaskPriority> | null;
  due_date: string | null;
  description: string | null;
};

type SelectType<T> = {
  label: string;
  value: T;
};

const commonInputProps: Pick<TextFieldProps, "sx" | "size" | "InputLabelProps"> = {
  // sx: {
  //   ".MuiOutlinedInput-root": {
  // width: "310px"
  //   }
  // },
  size: "small",
  InputLabelProps: { shrink: true }
};

interface Props extends Pick<FormikProps<FieldsState>, "setFieldValue" | "values" | "errors" | "setErrors"> {
  menu?: JSX.Element;
  isStatusDisabled?: boolean;
  statusAndPriorityInRow?: boolean;
  disabled?: boolean;
}

export const FieldsSet: React.FC<Props> = ({
  setFieldValue,
  values,
  menu,
  isStatusDisabled = false,
  errors,
  setErrors,
  statusAndPriorityInRow = false,
  disabled = false
}) => {
  const { getDealsAction } = useTaskManageActions();
  const [error, setError] = useState<DateValidationError | null>(null);
  // Show the description as linkified read-only text; click (when editable) to edit.
  const [editingDescription, setEditingDescription] = useState(false);
  const dateFormat = useDateFormatSelect();
  const { title, deal, assignee, status, priority, due_date, description } = values;
  const dealId = useDealIdSelector();
  const clientId = useClientIdSelector();
  const deals = useTaskDealsSelect();
  const assignees = useManagersSelect();
  const openPrompt = usePrompt();
  const canCreateDeal = useCanCreateDeal();

  // const { noticeModal } = useModals();

  const clearError = useCallback(
    (field: keyof FieldsState) => {
      if (!errors[field]) return;
      const errorsCopy = { ...errors };
      delete errorsCopy[field];
      setErrors(errorsCopy);
    },
    [errors]
  );

  const changeWithError = useCallback(
    (field: keyof FieldsState, value: any) => {
      clearError(field);
      setFieldValue(field, value);
    },
    [clearError, setFieldValue]
  );

  const errorMessage = React.useMemo(() => {
    switch (error) {
      case "disablePast": {
        return "Due date can be today. It can not be yesterday.";
      }
      case "invalidDate": {
        return "Your date is not valid";
      }

      default: {
        return "";
      }
    }
  }, [error]);

  const createDeal = async () => {
    const data = await openPrompt({ Component: DealCreate, props: {} });
    if (!data) return;
    await getDealsAction();
    changeWithError("deal", data);
  };

  return (
    <>
      <div className="title-row">
        <TextField
          sx={{ flexGrow: 1 }}
          value={title}
          placeholder="Enter Title"
          variant="standard"
          name="title"
          onChange={(e) => changeWithError("title", e.target.value)}
          error={!!errors.title}
          helperText={errors.title}
          disabled={disabled}
        />
        {menu || null}
      </div>
      <div className="fields-limit-box">
        <ToggleButtonGroup
          color="primary"
          value={status}
          exclusive
          fullWidth
          disabled={isStatusDisabled || disabled}
          onChange={(_, option) => {
            changeWithError("status", option);
          }}
        >
          {taskStatusOptions.map((availableStatus, index: number) => {
            return (
              <ToggleButton
                selected={isEqual(status, availableStatus)}
                value={availableStatus}
                key={index}
                sx={{
                  textTransform: "none",
                  fontFamily: "Roboto",
                  fontSize: "16px",
                  padding: "6px",
                  fontWeight: "400",
                  "&.MuiToggleButton-root.Mui-selected": {
                    backgroundColor: statusColors[status.value]
                  }
                }}
              >
                {startCase(availableStatus.label)}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
        {/* Hide the deal selector when the task is already scoped to a deal or a client */}
        {!dealId && !clientId && (
          <Autocomplete
            clearOnBlur
            value={deal}
            sx={{
              width: "306px",
              position: "relative",
              "& .MuiPaper-root::-webkit-scrollbar": {
                width: "20px"
              }
            }}
            onChange={(_, option) => changeWithError("deal", option)}
            PaperComponent={({ children }) => {
              return (
                <Paper
                  sx={{
                    position: "absolute",
                    maxWidth: "306px",
                    backgroundColor: "white",
                    zIndex: "10",
                    borderRadius: "10px",
                    "& .MuiAutocomplete-option:hover": {
                      backgroundColor: "#edf1f9"
                    }
                  }}
                >
                  <>
                    {children}
                    {canCreateDeal && (
                      <Button
                        onMouseDown={() => {
                          createDeal();
                        }}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          height: "50px",
                          backgroundColor: "#fff",
                          boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                          borderRadius: "0 0 10px 10px"
                        }}
                        fullWidth
                      >
                        Add Deal
                      </Button>
                    )}
                  </>
                </Paper>
              );
            }}
            getOptionLabel={(value) => {
              return value.title;
            }}
            options={deals || []}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: DealBase) => (
              <li {...props} key={option.id}>
                {option.title}
              </li>
            )}
            renderInput={(params) => {
              return (
                <TextField
                  sx={{
                    maxWidth: "306px",
                    ".MuiFormHelperText-root": {
                      top: "38px",
                      position: "absolute",
                      margin: 0
                    }
                  }}
                  {...params}
                  label="Deal"
                  error={!!errors.deal}
                  helperText={errors.deal}
                />
              );
            }}
          />
        )}
        <Autocomplete
          options={assignees}
          value={assignee}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: UserLight) => (
            <li {...props} key={option.id}>
              {option.full_name}
            </li>
          )}
          onChange={(_, option) => changeWithError("assignee", option)}
          getOptionLabel={(option) => option.full_name}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              {...commonInputProps}
              label="Assignee"
              error={!!errors.assignee}
              helperText={errors.assignee}
              disabled={disabled}
            />
          )}
        />
        <div className={classNames("pair-fields", { "pair-fields--row": statusAndPriorityInRow })}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            slotProps={{
              inputAdornment: { position: "start" },
              popper: {
                sx: {
                  minWidth: "335px",
                  ".MuiPickersCalendarHeader-labelContainer": {
                    fontSize: "0.9rem"
                  }
                }
              },
              textField: {
                sx: { padding: 0, svg: { color: "#0a3142" } },
                helperText: errors.due_date || errorMessage,
                error: !!errors.due_date || !!error,
                placeholder: "dd / mm / yyyy",
                fullWidth: true
              },
              field: {
                clearable: true,
                onClear: () => changeWithError("due_date", null)
              }
            }}
            value={due_date ? dayjs(due_date) : null}
            label="Due Date"
            disablePast
            disabled={disabled}
            onChange={(value) => {
              if (value && value.isValid()) {
                changeWithError("due_date", toIsoMidday(value.toISOString()));
              }
              // if (value && value.isBefore(today)) {
              //   return noticeModal({
              //     msg: "Due date can be today. it can not be yesterday."
              //   });
              // }
            }}
            onError={(newError) => setError(newError)}
          />
          <Autocomplete
            options={taskPrioritiesOptions}
            value={priority}
            fullWidth
            renderOption={(props, { value, label }) => (
              <li {...props} key={value}>
                {label}
              </li>
            )}
            disabled={disabled}
            onChange={(_, option) => changeWithError("priority", option)}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  sx: { fontSize: "17px", padding: "6px 6px !important" },
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SquareRoundedIcon
                        sx={{ fill: priority ? priorityColors[priority.value] : "#808080" }}
                        fontSize="md"
                      />
                    </InputAdornment>
                  )
                }}
                {...commonInputProps}
                disabled={disabled}
                label="Priority"
                error={!!errors.priority}
                helperText={errors.priority}
              />
            )}
          />
        </div>
      </div>
      <div className="textarea-wrapper">
        {editingDescription ? (
          <TextareaAutosize
            className="textarea"
            autoFocus
            maxRows={12}
            value={description || ""}
            disabled={disabled}
            onChange={(event) => {
              changeWithError("description", event.target.value);
            }}
            onBlur={() => setEditingDescription(false)}
          />
        ) : (
          <Box
            className="textarea"
            sx={{ ...readDescriptionStyle, cursor: disabled ? "default" : "text" }}
            onClick={() => {
              if (!disabled) setEditingDescription(true);
            }}
          >
            {description ? (
              renderDescriptionWithLinks(description)
            ) : (
              <Box component="span" sx={{ color: "#90a4ae" }}>
                {disabled ? "" : "Add a description"}
              </Box>
            )}
          </Box>
        )}
      </div>
    </>
  );
};
